import React from "react";
import {
  Alert,
  Box,
  Button,
  ProgressBar,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OmsExportRequest } from "../../lusca-search-api/generated-src";
import useOmsExport from "../../hooks/useOmsExport";
import { fileSize } from "./omsReportFormatting";

export type OmsExportState = ReturnType<typeof useOmsExport>;
export function OMSExportActions({
  state,
  mode,
  filters = [],
  operation = "and",
  valid = true,
}: {
  state: OmsExportState;
  mode: OmsExportRequest["mode"];
  filters?: OmsExportRequest["filters"];
  operation?: OmsExportRequest["operation"];
  valid?: boolean;
}) {
  const job = state.job.data;
  if (state.expired || state.unavailable)
    return (
      <Button variant="primary" onClick={state.reset}>
        Create new export
      </Button>
    );
  if (state.job.isError && state.jobId)
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button onClick={state.reset}>Start new export</Button>
        <Button
          variant="primary"
          loading={state.job.isFetching}
          loadingText="Checking export"
          onClick={() => {
            state.job.refetch().catch(() => undefined);
          }}
        >
          Retry status
        </Button>
      </SpaceBetween>
    );
  if (job?.status === "READY")
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button onClick={state.reset}>
          {mode === "full" || (job.csvFiles || 1) > 1
            ? "Create new export"
            : "Change filters"}
        </Button>
        <Button
          variant="primary"
          iconName="download"
          wrapText={false}
          loading={state.download.isLoading}
          loadingText="Preparing download"
          onClick={() => state.download.mutate()}
        >
          Download ZIP
        </Button>
      </SpaceBetween>
    );
  if (job?.status === "FAILED")
    return (
      <Button variant="primary" onClick={state.reset}>
        Start new export
      </Button>
    );
  if (state.jobId)
    return (
      <Button variant="primary" disabled>
        Creating export
      </Button>
    );
  return (
    <Button
      variant="primary"
      wrapText={false}
      loading={state.create.isLoading}
      loadingText="Starting export"
      disabled={!valid}
      onClick={() => state.create.mutate({ filters, operation })}
    >
      Create export
    </Button>
  );
}
export default function OMSExportPanel({
  state,
  mode,
}: {
  state: OmsExportState;
  mode: OmsExportRequest["mode"];
}) {
  const job = state.job.data;
  if (state.expired || state.unavailable)
    return (
      <Alert
        type="info"
        header={state.expired ? "Export expired" : "Export no longer available"}
      >
        {mode === "filtered"
          ? "Choose Create new export to review your filters and prepare another download."
          : "Choose Create new export to prepare another download of this scheduled report."}
      </Alert>
    );
  const createError = state.create.error as {
    response?: { status?: number; data?: { error?: string; message?: string } };
  } | null;
  return (
    <SpaceBetween size="s">
      {state.create.isError && (
        <Alert type="error" header="Could not start export">
          {createError?.response?.status === 400
            ? createError.response.data?.error ||
              createError.response.data?.message ||
              "Check your filters, then choose Create export again."
            : "Choose Create export again to retry."}
        </Alert>
      )}
      {state.job.isError && state.jobId && (
        <Alert type="error" header="Export status unavailable">
          Your export may still be running. Choose Retry status to check.
        </Alert>
      )}
      {state.jobId && !job && !state.job.isError && (
        <div role="status">Checking export status…</div>
      )}
      {(job?.status === "PREPARING" || state.create.isLoading) && (
        <ProgressBar
          value={
            job?.totalParts ? (job.completedParts / job.totalParts) * 100 : 0
          }
          label="Creating your export"
          description="You can close this window and return later. Your export will continue in the background."
          additionalInfo={
            job && job.completedParts === job.totalParts
              ? "Finalising the ZIP download…"
              : "Processing the scheduled report…"
          }
        />
      )}
      {job?.status === "FAILED" && (
        <Alert type="error" header="Export could not be completed">
          {job.error || "Start a new export to try again."}
        </Alert>
      )}
      {job?.status === "READY" && (
        <SpaceBetween size="s">
          <Alert type="success" header="Export ready">
            {job.rowCount?.toLocaleString()} rows ·{" "}
            {fileSize(job.sizeBytes || 0)} ZIP
            {job.rowCount === 0 &&
              (mode === "full" || job.sourceRows === 0
                ? ". This report is empty. The CSV contains column headers."
                : ". No rows matched your filters. The CSV contains column headers.")}
          </Alert>
          <Box color="text-body-secondary">
            Download available until{" "}
            {new Intl.DateTimeFormat(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              timeZoneName: "short",
            }).format(new Date(job.expiresAt))}
            .
          </Box>
        </SpaceBetween>
      )}
      {state.download.isError && (
        <Alert type="error" header="Could not start download">
          Choose Download ZIP again to get a fresh download link.
        </Alert>
      )}
      {state.sent && (
        <div role="status" aria-live="polite">
          <Box color="text-body-secondary">
            Sent to your browser. Check your browser’s downloads for progress.
          </Box>
        </div>
      )}
    </SpaceBetween>
  );
}
